import { FormEvent, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import FinanceDateFilter from "../../components/finance/FinanceDateFilter";
import FinanceFieldCostsTable from "../../components/finance/FinanceFieldCostsTable";
import FinanceFilterField from "../../components/finance/FinanceFilterField";
import FinanceMultiSelect from "../../components/finance/FinanceMultiSelect";
import FinanceFilterPanel from "../../components/finance/FinanceFilterPanel";
import FinanceHelpLabel from "../../components/finance/FinanceHelpLabel";
import FinanceIncompleteBadge from "../../components/finance/FinanceIncompleteBadge";
import FinanceMetricCard from "../../components/finance/FinanceMetricCard";
import FinancePagination from "../../components/finance/FinancePagination";
import FinanceWarningBanner from "../../components/finance/FinanceWarningBanner";
import TableLoadingSkeleton from "../../components/shared/TableLoadingSkeleton";
import { CopyIdentifierButton } from "../../components/controls/CopyableIdentifier";
import { fetchFinanceProduction } from "../../finance/api";
import {
  financeRangeFromSearch,
  type FinanceDateRangeValue,
} from "../../finance/dates";
import { formatFinanceQuantity } from "../../finance/quantities";
import {
  formatMoney,
  formatPercent,
  knownMoney,
  previousPeriodComparison,
} from "../../finance/formatting";
import type {
  FinanceProductionResponse,
  FinanceProductionFilters,
  FinanceProductionGroup,
  FinanceProductionRoomGroup,
  FinanceProductionRun,
  FinanceProductionSummary,
} from "../../finance/types";
import {
  roomLabel,
  roomsLabel,
  wipLabel,
  wipsLabel,
} from "../../shared/tenantLabels";

const initialFilters: FinanceProductionFilters = {
  classIds: [],
  categoryIds: [],
  productIds: [],
  rooms: [],
  costStatuses: [],
  fieldStatuses: [],
  groupBy: "class",
};

function materialCostValue(summary: FinanceProductionSummary) {
  return knownMoney(summary.material_cost, summary.known_material_cost);
}

function productionGroupLabel(
  groupBy: FinanceProductionResponse["group_by"],
  isFarming = false,
) {
  switch (groupBy) {
    case "class":
      return "Class";
    case "category":
      return "Category";
    case "product":
      return isFarming ? "Crop" : "Product";
    default:
      return isFarming ? "Field" : "WIP";
  }
}

export default function FinanceProduction() {
  const tenant = useSelector((state: any) => state.auth.tenant);
  const roomEntityLabel = roomLabel(tenant);
  const roomsEntityLabel = roomsLabel(tenant);
  const wipEntityLabel = wipLabel(tenant);
  const wipsEntityLabel = wipsLabel(tenant);
  const productionSearchPlaceholder = `Search ${wipEntityLabel.toLowerCase()} or ${roomEntityLabel.toLowerCase()}`;
  const [range, setRange] = useState<FinanceDateRangeValue>(() =>
    financeRangeFromSearch(window.location.search),
  );
  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] =
    useState<FinanceProductionFilters>(initialFilters);
  const [report, setReport] = useState<FinanceProductionResponse | null>(null);
  const viewInitialized = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    fetchFinanceProduction(range, page, search, filters)
      .then((response) => {
        if (active) {
          setReport(response);
          if (!viewInitialized.current) {
            viewInitialized.current = true;
            if (response.view_mode === "farming") {
              setFilters((current) =>
                current.groupBy === "class"
                  ? { ...current, groupBy: "room" }
                  : current,
              );
            }
          }
        }
      })
      .catch(() => {
        if (active) {
          setError("Production costs could not be loaded.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [
    range.from,
    range.to,
    page,
    search,
    filters.classIds,
    filters.categoryIds,
    filters.productIds,
    filters.rooms,
    filters.costStatuses,
    filters.fieldStatuses,
    filters.groupBy,
  ]);

  const updateFilter = <K extends keyof FinanceProductionFilters>(
    key: K,
    value: FinanceProductionFilters[K],
  ) => {
    setFilters((current) => {
      if (key === "classIds") {
        return {
          ...current,
          classIds: value as number[],
          categoryIds: [],
          productIds: [],
        };
      }
      if (key === "categoryIds") {
        return {
          ...current,
          categoryIds: value as number[],
          productIds: [],
        };
      }
      return { ...current, [key]: value };
    });
    setPage(1);
  };

  const changeRange = (value: FinanceDateRangeValue) => {
    setRange(value);
    setPage(1);
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setSearch(searchDraft.trim());
    setPage(1);
  };

  const summary = report?.summary;
  const previous = report?.previous_summary;
  const previousRange = report?.range
    ? { from: report.range.previous_from, to: report.range.previous_to }
    : null;
  const isFarming = report?.view_mode === "farming";
  const hasWarnings =
    summary !== undefined &&
    (summary.missing_cost_inputs > 0 || summary.wips_without_inputs > 0);
  const hasFilters =
    search !== "" ||
    filters.groupBy !== (isFarming ? "room" : initialFilters.groupBy) ||
    filters.classIds.length > 0 ||
    filters.categoryIds.length > 0 ||
    filters.productIds.length > 0 ||
    filters.rooms.length > 0 ||
    filters.costStatuses.length > 0 ||
    filters.fieldStatuses.length > 0;

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-6 sm:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-medium text-emerald-700">Finance</p>
            <h1 className="text-2xl font-semibold text-slate-900">
              {isFarming ? "Field Costs" : "Production Costs"}
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              {isFarming
                ? "Track the cost invested in active fields and final harvest costs for completed fields."
                : "Material cost, output cost per unit, and compatible-unit yield for completed WIPs."}
            </p>
          </div>
          <FinanceDateFilter value={range} onChange={changeRange} />
        </div>

        {hasWarnings && (
          <div className="mt-5">
            <FinanceWarningBanner>
              {summary.missing_cost_inputs > 0 && (
                <span>
                  {summary.missing_cost_inputs} consumed input
                  {summary.missing_cost_inputs === 1 ? " is" : "s are"} missing
                  material cost.{" "}
                </span>
              )}
              {summary.wips_without_inputs > 0 && (
                <span>
                  {summary.wips_without_inputs} completed{" "}
                  {isFarming ? "field" : "WIP"}
                  {summary.wips_without_inputs === 1 ? " has" : "s have"} no
                  active input records.
                </span>
              )}
            </FinanceWarningBanner>
          </div>
        )}

        <section
          className={`mt-5 grid gap-4 sm:grid-cols-2 ${
            isFarming ? "xl:grid-cols-5" : "xl:grid-cols-4"
          }`}
        >
          {isFarming ? (
            <>
              <FinanceMetricCard
                label="Fields in Progress"
                value={summary?.fields_in_progress ?? "—"}
              />
              <FinanceMetricCard
                label="Invested in Active Fields"
                value={
                  summary
                    ? knownMoney(
                        summary.active_field_cost,
                        summary.known_active_field_cost,
                      )
                    : "—"
                }
                incomplete={summary?.active_field_cost === null}
              />
              <FinanceMetricCard
                label="Completed Fields"
                value={summary?.completed_fields ?? "—"}
                comparison={
                  summary && previous
                    ? previousPeriodComparison(
                        summary.completed_fields,
                        previous.completed_fields,
                        previousRange,
                      )
                    : null
                }
              />
              <FinanceMetricCard
                label="Completed Field Costs"
                value={
                  summary
                    ? knownMoney(
                        summary.completed_field_cost,
                        summary.known_completed_field_cost,
                      )
                    : "—"
                }
                comparison={
                  summary && previous
                    ? previousPeriodComparison(
                        summary.completed_field_cost,
                        previous.completed_field_cost,
                        previousRange,
                      )
                    : null
                }
                incomplete={summary?.completed_field_cost === null}
              />
              <FinanceMetricCard
                label="Fields With Missing Costs"
                value={summary?.missing_cost_fields ?? "—"}
              />
            </>
          ) : (
            <>
              <FinanceMetricCard
                label="Completed WIPs"
                value={summary?.completed_wips ?? "—"}
                comparison={
                  summary && previous
                    ? previousPeriodComparison(
                        summary.completed_wips,
                        previous.completed_wips,
                        previousRange,
                      )
                    : null
                }
              />
              <FinanceMetricCard
                label="Material Cost"
                value={summary ? materialCostValue(summary) : "—"}
                comparison={
                  summary && previous
                    ? previousPeriodComparison(
                        summary.material_cost,
                        previous.material_cost,
                        previousRange,
                      )
                    : null
                }
                incomplete={summary?.complete === false}
              />
              <FinanceMetricCard
                label="Average Cost per WIP"
                value={formatMoney(summary?.average_material_cost_per_wip)}
                comparison={
                  summary && previous
                    ? previousPeriodComparison(
                        summary.average_material_cost_per_wip,
                        previous.average_material_cost_per_wip,
                        previousRange,
                      )
                    : null
                }
                incomplete={summary?.complete === false}
              />
              <FinanceMetricCard
                label="Missing Input Costs"
                value={summary?.missing_cost_inputs ?? "—"}
              />
            </>
          )}
        </section>

        <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <FinanceFilterPanel
            additionalFilterCount={
              isFarming
                ? Number(filters.classIds.length > 0) +
                  Number(filters.categoryIds.length > 0)
                : Number(filters.costStatuses.length > 0)
            }
            hasActiveFilters={hasFilters}
            onClear={() => {
              setFilters({
                ...initialFilters,
                groupBy: isFarming ? "room" : initialFilters.groupBy,
              });
              setSearch("");
              setSearchDraft("");
              setPage(1);
            }}
            primary={
              <>
                <FinanceFilterField label="Grouping">
                  <select
                    value={filters.groupBy}
                    onChange={(event) =>
                      updateFilter(
                        "groupBy",
                        event.target
                          .value as FinanceProductionFilters["groupBy"],
                      )
                    }
                  >
                    <option value="class">Class</option>
                    <option value="category">Category</option>
                    <option value="product">
                      {isFarming ? "Crop" : "Product"}
                    </option>
                    <option value="room">{roomEntityLabel}</option>
                    <option value="wip">
                      {wipEntityLabel}
                    </option>
                  </select>
                </FinanceFilterField>
                {isFarming && (
                  <FinanceFilterField label="Field status">
                    <FinanceMultiSelect<"in_progress" | "completed">
                      value={filters.fieldStatuses}
                      placeholder="All statuses"
                      options={[
                        { label: "In progress", value: "in_progress" },
                        { label: "Completed", value: "completed" },
                      ]}
                      onChange={(value) => updateFilter("fieldStatuses", value)}
                    />
                  </FinanceFilterField>
                )}
                {!isFarming && (
                  <FinanceFilterField label="Class">
                    <FinanceMultiSelect
                      value={filters.classIds}
                      placeholder="All classes"
                      options={
                        report?.filters.classes.map((option) => ({
                          label: option.name,
                          value: option.id,
                        })) ?? []
                      }
                      onChange={(value) => updateFilter("classIds", value)}
                    />
                  </FinanceFilterField>
                )}
                {!isFarming && (
                  <FinanceFilterField label="Category">
                    <FinanceMultiSelect
                      value={filters.categoryIds}
                      placeholder="All categories"
                      options={
                        report?.filters.categories
                          .filter(
                            (option) =>
                              filters.classIds.length === 0 ||
                              (option.class_id !== null &&
                                option.class_id !== undefined &&
                                filters.classIds.includes(option.class_id)),
                          )
                          .map((option) => ({
                            label: option.name,
                            value: option.id,
                          })) ?? []
                      }
                      onChange={(value) => updateFilter("categoryIds", value)}
                    />
                  </FinanceFilterField>
                )}
                <FinanceFilterField label={isFarming ? "Crop" : "Product"}>
                  <FinanceMultiSelect
                    value={filters.productIds}
                    placeholder={isFarming ? "All crops" : "All products"}
                    options={
                      report?.filters.products
                        .filter(
                          (option) =>
                            (filters.classIds.length === 0 ||
                              (option.class_id !== null &&
                                option.class_id !== undefined &&
                                filters.classIds.includes(option.class_id))) &&
                            (filters.categoryIds.length === 0 ||
                              (option.category_id !== null &&
                                option.category_id !== undefined &&
                                filters.categoryIds.includes(
                                  option.category_id,
                                ))),
                        )
                        .map((option) => ({
                          label: option.name,
                          value: option.id,
                        })) ?? []
                    }
                    onChange={(value) => updateFilter("productIds", value)}
                  />
                </FinanceFilterField>
                <FinanceFilterField
                  label={isFarming ? `${roomEntityLabel} / plot` : roomEntityLabel}
                >
                  <FinanceMultiSelect
                    value={filters.rooms}
                    placeholder={`All ${roomsEntityLabel.toLowerCase()}`}
                    options={
                      report?.filters.rooms.map((option) => ({
                        label: option.name,
                        value: option.name,
                      })) ?? []
                    }
                    onChange={(value) => updateFilter("rooms", value)}
                  />
                </FinanceFilterField>
                {isFarming && (
                  <FinanceFilterField label="Cost status">
                    <FinanceMultiSelect
                      value={filters.costStatuses}
                      placeholder="All statuses"
                      options={[
                        { label: "Complete", value: "complete" },
                        { label: "Missing costs", value: "missing_costs" },
                        {
                          label: "Pending harvest",
                          value: "pending_harvest",
                        },
                      ]}
                      onChange={(value) => updateFilter("costStatuses", value)}
                    />
                  </FinanceFilterField>
                )}
              </>
            }
            additional={
              isFarming ? (
                <>
                  <FinanceFilterField label="Class">
                    <FinanceMultiSelect
                      value={filters.classIds}
                      placeholder="All classes"
                      options={
                        report?.filters.classes.map((option) => ({
                          label: option.name,
                          value: option.id,
                        })) ?? []
                      }
                      onChange={(value) => updateFilter("classIds", value)}
                    />
                  </FinanceFilterField>
                  <FinanceFilterField label="Category">
                    <FinanceMultiSelect
                      value={filters.categoryIds}
                      placeholder="All categories"
                      options={
                        report?.filters.categories
                          .filter(
                            (option) =>
                              filters.classIds.length === 0 ||
                              (option.class_id !== null &&
                                option.class_id !== undefined &&
                                filters.classIds.includes(option.class_id)),
                          )
                          .map((option) => ({
                            label: option.name,
                            value: option.id,
                          })) ?? []
                      }
                      onChange={(value) => updateFilter("categoryIds", value)}
                    />
                  </FinanceFilterField>
                </>
              ) : (
                <FinanceFilterField label="Cost status">
                  <FinanceMultiSelect
                    value={filters.costStatuses}
                    placeholder="All statuses"
                    options={[
                      { label: "Complete", value: "complete" },
                      { label: "Missing costs", value: "incomplete" },
                    ]}
                    onChange={(value) => updateFilter("costStatuses", value)}
                  />
                </FinanceFilterField>
              )
            }
          />
          <div className="flex flex-col gap-3 border-b border-slate-200 p-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="font-semibold text-slate-900">
                {isFarming
                  ? "Fields and harvest costs"
                  : "Completed production runs"}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {isFarming
                  ? "Active fields show their full recorded cost to date, even before harvest."
                  : "Costs recalculate from the latest saved WipSync records."}
              </p>
            </div>
            <form className="flex gap-2" onSubmit={submitSearch}>
              <label className="sr-only" htmlFor="production-search">
                {productionSearchPlaceholder}
              </label>
              <input
                id="production-search"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder={productionSearchPlaceholder}
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
              />
              <button
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                type="submit"
              >
                Search
              </button>
            </form>
          </div>

          {error && (
            <p className="p-5 text-sm font-medium text-red-700">{error}</p>
          )}

          {!error && loading && (
            <div className="p-3">
              <TableLoadingSkeleton rows={7} columns={6} />
            </div>
          )}

          {!error && !loading && report?.data.length === 0 && (
            <p className="p-5 text-sm text-slate-500">
              {isFarming
                ? "No fields were found."
                : "No completed WIPs were found for this period."}
            </p>
          )}

          {!error && !loading && report && report.data.length > 0 && (
            <>
              {report.group_by !== "wip" && report.group_by !== "room" && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      <tr>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel
                            label={productionGroupLabel(
                              report.group_by,
                              isFarming,
                            )}
                            help={
                              isFarming
                                ? "The Class, Category, or Crop used to group fields with recorded harvest output."
                                : "The Class, Category, or Product used to group completed production runs."
                            }
                          />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel
                            label={isFarming ? "Fields" : "Production Runs"}
                            help={
                              isFarming
                                ? "Number of fields with harvest output included in this group."
                                : "Number of completed WIPs included in this group."
                            }
                          />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel
                            label={isFarming ? "Harvest Output" : "Output"}
                            help={`Total ${
                              isFarming ? "harvest" : "finished"
                            } output for this group. Kilograms and pieces are kept in separate rows.`}
                          />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel
                            label={isFarming ? "Field Cost" : "Material Cost"}
                          />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel
                            label={
                              isFarming
                                ? "Average Cost per Field"
                                : "Average Cost per WIP"
                            }
                          />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel
                            label={
                              isFarming
                                ? "Cost per Harvest Unit"
                                : "Cost per Output Unit"
                            }
                          />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel
                            label={
                              isFarming
                                ? "Average Harvest Yield"
                                : "Average Yield"
                            }
                            help="Average finished-output yield across WIPs with compatible input and output units."
                          />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel
                            label="Status"
                            help="Complete when every included production run has the material costs needed for this calculation."
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(report.data as FinanceProductionGroup[]).map(
                        (group) => (
                          <tr key={group.key}>
                            <td className="px-4 py-4 font-semibold text-slate-900">
                              {group.group_name}
                            </td>
                            <td className="px-4 py-4 text-slate-700">
                              {group.production_runs}
                            </td>
                            <td className="whitespace-nowrap px-4 py-4 text-slate-700">
                              {group.output_quantity.toLocaleString("en-CA")}{" "}
                              {group.output_unit}
                            </td>
                            <td className="whitespace-nowrap px-4 py-4 font-medium text-slate-900">
                              {knownMoney(
                                group.material_cost,
                                group.known_material_cost,
                              )}
                            </td>
                            <td className="whitespace-nowrap px-4 py-4 text-slate-700">
                              {formatMoney(group.average_material_cost_per_wip)}
                            </td>
                            <td className="whitespace-nowrap px-4 py-4 text-slate-700">
                              {group.material_cost_per_output_unit === null
                                ? "Incomplete"
                                : `${formatMoney(group.material_cost_per_output_unit)} / ${group.output_unit}`}
                            </td>
                            <td className="whitespace-nowrap px-4 py-4 text-slate-700">
                              {formatPercent(group.average_yield_percentage)}
                            </td>
                            <td className="px-4 py-4">
                              {isFarming && group.provisional ? (
                                <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-800">
                                  Provisional
                                </span>
                              ) : group.cost_complete ? (
                                <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800">
                                  Complete
                                </span>
                              ) : (
                                <FinanceIncompleteBadge
                                  count={group.missing_cost_count}
                                />
                              )}
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              {report.group_by === "room" && (
                <FinanceFieldCostsTable
                  groups={report.data as FinanceProductionRoomGroup[]}
                  isFarming={isFarming}
                  roomEntityLabel={roomEntityLabel}
                  wipEntityLabel={wipEntityLabel}
                  wipsEntityLabel={wipsEntityLabel}
                />
              )}
              {report.group_by === "wip" && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      <tr>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label={wipEntityLabel} />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel
                            label={isFarming ? "Harvest Output" : "Products"}
                            help={
                              isFarming
                                ? "Harvest products recorded for this field. Active fields can remain pending until harvest."
                                : "Finished products and allocated material cost recorded for this WIP."
                            }
                          />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel
                            label={
                              isFarming ? "Inputs / Harvest" : "Input / Output"
                            }
                          />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel
                            label={isFarming ? "Cost to Date" : "Material Cost"}
                          />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel
                            label={
                              isFarming
                                ? "Cost per Harvest Unit"
                                : "Cost per Output Unit"
                            }
                          />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel
                            label={isFarming ? "Harvest Yield" : "Yield"}
                          />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel
                            label="Status"
                            help={
                              isFarming
                                ? "Shows whether the field is in progress, awaiting harvest, or missing recorded costs."
                                : "Complete when this WIP has all material costs required for the calculation."
                            }
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(report.data as FinanceProductionRun[]).map((run) => (
                        <tr key={run.id} className="align-top">
                          <td className="px-4 py-4">
                            <div className="relative inline-block pr-4">
                              <a
                                className="font-semibold text-emerald-700 hover:underline"
                                href={`/wips/${run.id}`}
                              >
                                {run.lot_number}
                              </a>
                              <CopyIdentifierButton
                                value={run.lot_number}
                                description="lot number"
                                wrapperClassName="absolute -right-1 -top-1.5"
                              />
                            </div>
                            <p className="mt-1 text-xs text-slate-500">
                              {run.room_name || `No ${roomEntityLabel.toLowerCase()}`}
                            </p>
                            <p className="text-xs text-slate-500">
                              {isFarming && !run.is_closed
                                ? `Started ${
                                    run.started_at
                                      ? new Date(
                                          run.started_at,
                                        ).toLocaleDateString("en-CA")
                                      : "date unavailable"
                                  }`
                                : run.completed_at
                                  ? `${isFarming ? "Completed " : ""}${new Date(
                                      run.completed_at,
                                    ).toLocaleDateString("en-CA")}`
                                  : "Completion date unavailable"}
                            </p>
                          </td>
                          <td className="px-4 py-4">
                            <div className="space-y-2">
                              {isFarming && run.products.length === 0 && (
                                <p className="text-sm text-slate-500">
                                  Pending harvest
                                </p>
                              )}
                              {run.products.map((product) => (
                                <div
                                  key={`${product.product_id}-${product.plu}-${product.unit}`}
                                >
                                  <p className="font-medium text-slate-800">
                                    {product.product_name || product.plu}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {product.quantity.toLocaleString("en-CA")}{" "}
                                    {product.unit} ·{" "}
                                    {formatMoney(
                                      product.allocated_material_cost,
                                    )}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 text-slate-700">
                            <p>{formatFinanceQuantity(run, "input")} in</p>
                            <p className="mt-1">
                              {isFarming && !run.harvest_recorded
                                ? "Pending harvest"
                                : `${formatFinanceQuantity(run, "output")} out`}
                            </p>
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 font-medium text-slate-900">
                            {knownMoney(
                              run.material_cost,
                              run.known_material_cost,
                            )}
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 text-slate-700">
                            {run.material_cost_per_output_unit === null
                              ? isFarming && !run.harvest_recorded
                                ? "Pending harvest"
                                : "Incomplete"
                              : `${formatMoney(
                                  run.material_cost_per_output_unit,
                                )} / ${run.output_unit}${
                                  isFarming && !run.is_closed
                                    ? " · Provisional"
                                    : ""
                                }`}
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 text-slate-700">
                            {isFarming && !run.harvest_recorded
                              ? "Pending harvest"
                              : formatPercent(run.yield_percentage)}
                          </td>
                          <td className="px-4 py-4">
                            {isFarming ? (
                              <div className="space-y-2">
                                <span
                                  className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                    run.is_closed
                                      ? "bg-emerald-100 text-emerald-800"
                                      : "bg-blue-100 text-blue-800"
                                  }`}
                                >
                                  {run.is_closed ? "Completed" : "In Progress"}
                                </span>
                                {run.cost_status === "missing_costs" && (
                                  <FinanceIncompleteBadge
                                    count={
                                      run.missing_cost_inputs +
                                      (run.is_closed
                                        ? run.missing_cost_outputs
                                        : 0)
                                    }
                                  />
                                )}
                                {run.cost_status === "pending_harvest" && (
                                  <span className="block text-xs font-medium text-slate-500">
                                    Pending harvest
                                  </span>
                                )}
                                {!run.is_closed && run.harvest_recorded && (
                                  <span className="block text-xs font-medium text-slate-500">
                                    Harvest cost is provisional
                                  </span>
                                )}
                              </div>
                            ) : (
                              <>
                                {run.cost_complete ? (
                                  <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800">
                                    Complete
                                  </span>
                                ) : (
                                  <FinanceIncompleteBadge
                                    count={
                                      run.missing_cost_inputs +
                                      run.missing_cost_outputs
                                    }
                                  />
                                )}
                                <p className="mt-2 text-xs text-slate-500">
                                  {run.allocation_percentage.toFixed(1)}% cost
                                  allocated
                                </p>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <FinancePagination
                label={
                  report.group_by === "wip"
                    ? wipsEntityLabel
                    : report.group_by === "room"
                      ? roomsEntityLabel
                    : `${report.group_by} groups`
                }
                page={report.pagination.current_page}
                lastPage={report.pagination.last_page}
                total={report.pagination.total}
                onPageChange={setPage}
              />
            </>
          )}
        </section>
      </div>
    </main>
  );
}
